import { encodeFunctionData, getAddress, isAddress } from "viem";
import { PROTOCOL_CONFIG_ABI, SYNDICATE_GOVERNOR_ABI } from "../abis.js";
import { usage } from "../errors.js";
const ZERO_VALUE = "0x0";
export const VOTE_TYPES = {
    For: 0,
    Against: 1,
    Abstain: 2,
};
/**
 * Per-vault governor (PR #421): there is no global governor singleton any
 * more — every write targets the vault's own governor. Callers resolve it
 * with `resolveGovernor(client, chainId, vault)` (factory.governorOf) and
 * pass it in each encoder's `governor` arg. Encoders stay pure (no RPC).
 */
function governorTx(governor, chainId, data) {
    return {
        to: governor,
        data,
        value: ZERO_VALUE,
        chainId,
    };
}
function parseGovernor(value) {
    if (typeof value !== "string" || !isAddress(value)) {
        throw usage("`governor` must be the vault's governor address — resolve it with `resolveGovernor(client, chainId, vault)` (factory.governorOf)");
    }
    return getAddress(value);
}
function parseProposalId(value) {
    if (typeof value === "bigint") {
        if (value < 0n)
            throw usage("`proposalId` must be a non-negative integer");
        return value;
    }
    if (typeof value === "number") {
        if (!Number.isInteger(value) || value < 0) {
            throw usage("`proposalId` must be a non-negative integer");
        }
        return BigInt(value);
    }
    if (!/^\d+$/.test(value)) {
        throw usage(`\`proposalId\` must be a non-negative integer string, got "${value}"`);
    }
    return BigInt(value);
}
export function encodeVote(args, chainId) {
    const governor = parseGovernor(args.governor);
    const support = VOTE_TYPES[args.vote];
    if (support === undefined) {
        throw usage(`Unsupported vote "${args.vote}" — must be one of: ${Object.keys(VOTE_TYPES).join(", ")}`);
    }
    const proposalId = parseProposalId(args.proposalId);
    const data = encodeFunctionData({
        abi: SYNDICATE_GOVERNOR_ABI,
        functionName: "vote",
        args: [proposalId, support],
    });
    return {
        txs: [governorTx(governor, chainId, data)],
        preconditions: [],
        description: `Vote ${args.vote} on proposal ${proposalId.toString()}.`,
        note: "Voter must hold ERC20Votes shares of the vault at the proposal's snapshot timestamp; double-voting reverts. Use the proposal read endpoint to check `voteEnd` first.",
    };
}
function lifecycleAction(fn, proposalId, governor, chainId, description, note) {
    const data = encodeFunctionData({
        abi: SYNDICATE_GOVERNOR_ABI,
        functionName: fn,
        args: [proposalId],
    });
    return {
        txs: [governorTx(governor, chainId, data)],
        preconditions: [],
        description,
        note,
    };
}
export function encodeExecute(args, chainId) {
    const id = parseProposalId(args.proposalId);
    return lifecycleAction("executeProposal", id, parseGovernor(args.governor), chainId, `Execute proposal ${id.toString()} (open the strategy).`, "Reverts if the proposal is not in the `Approved` state. Check `state == Approved` via the proposal read endpoint first.");
}
export function encodeSettle(args, chainId) {
    const id = parseProposalId(args.proposalId);
    return lifecycleAction("settleProposal", id, parseGovernor(args.governor), chainId, `Settle proposal ${id.toString()} (close the strategy and distribute fees).`, "Proposer can settle anytime; anyone can settle after the strategy duration elapses.");
}
export function encodeCancel(args, chainId) {
    const id = parseProposalId(args.proposalId);
    return lifecycleAction("cancelProposal", id, parseGovernor(args.governor), chainId, `Cancel proposal ${id.toString()} (proposer-only).`, "Only the proposer can cancel, and only while the proposal is in `Pending`, `GuardianReview`, or `Approved`.");
}
export function encodeVeto(args, chainId) {
    const id = parseProposalId(args.proposalId);
    return lifecycleAction("vetoProposal", id, parseGovernor(args.governor), chainId, `Veto proposal ${id.toString()} (vault-owner only, only while \`Pending\`).`, "Vault owner only. Reverts after the proposal enters `GuardianReview`. To block at that point, depend on guardian block-quorum.");
}
export { parseProposalId };
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
function validateBatchCall(call, scope, idx) {
    if (!call || typeof call !== "object") {
        throw usage(`\`${scope}[${idx}]\` must be an object`);
    }
    const c = call;
    if (typeof c.target !== "string" || !isAddress(c.target)) {
        throw usage(`\`${scope}[${idx}].target\` must be a 0x-prefixed address`);
    }
    if (typeof c.data !== "string" || !/^0x[0-9a-fA-F]*$/.test(c.data)) {
        throw usage(`\`${scope}[${idx}].data\` must be a 0x-prefixed hex string`);
    }
    let value;
    if (typeof c.value === "string") {
        if (!/^\d+$/.test(c.value)) {
            throw usage(`\`${scope}[${idx}].value\` must be a non-negative integer string`);
        }
        value = BigInt(c.value);
    }
    else if (typeof c.value === "bigint") {
        if (c.value < 0n) {
            throw usage(`\`${scope}[${idx}].value\` must be non-negative`);
        }
        value = c.value;
    }
    else if (c.value === undefined || c.value === null) {
        value = 0n;
    }
    else {
        throw usage(`\`${scope}[${idx}].value\` must be a non-negative integer string (or omitted for 0)`);
    }
    return { target: getAddress(c.target), data: c.data, value };
}
export function encodePropose(args, chainId) {
    const vault = getAddress(args.vault);
    const governor = parseGovernor(args.governor);
    const strategy = args.strategy === ZERO_ADDRESS ? ZERO_ADDRESS : getAddress(args.strategy);
    if (typeof args.metadataURI !== "string" || args.metadataURI.length === 0) {
        throw usage("`metadataURI` must be a non-empty string (e.g. ipfs://...)");
    }
    const strategyDuration = typeof args.strategyDuration === "bigint"
        ? args.strategyDuration
        : BigInt(args.strategyDuration);
    if (strategyDuration <= 0n) {
        throw usage("`strategyDuration` must be > 0 (seconds)");
    }
    if (!Array.isArray(args.executeCalls)) {
        throw usage("`executeCalls` must be an array of {target, data, value} objects");
    }
    if (!Array.isArray(args.settlementCalls)) {
        throw usage("`settlementCalls` must be an array of {target, data, value} objects");
    }
    const executeCalls = args.executeCalls.map((c, i) => validateBatchCall(c, "executeCalls", i));
    const settlementCalls = args.settlementCalls.map((c, i) => validateBatchCall(c, "settlementCalls", i));
    const coProposers = (args.coProposers ?? []).map((cp, i) => {
        if (!cp || typeof cp !== "object") {
            throw usage(`\`coProposers[${i}]\` must be an object`);
        }
        const agent = cp.agent;
        const splitBps = cp.splitBps;
        if (typeof agent !== "string" || !isAddress(agent)) {
            throw usage(`\`coProposers[${i}].agent\` must be a 0x-prefixed address`);
        }
        let bps;
        if (typeof splitBps === "string" && /^\d+$/.test(splitBps)) {
            bps = BigInt(splitBps);
        }
        else if (typeof splitBps === "bigint") {
            bps = splitBps;
        }
        else if (typeof splitBps === "number" && Number.isInteger(splitBps) && splitBps >= 0) {
            bps = BigInt(splitBps);
        }
        else {
            throw usage(`\`coProposers[${i}].splitBps\` must be a non-negative integer (string, number, or bigint)`);
        }
        if (bps < 0n || bps > 10000n) {
            throw usage(`\`coProposers[${i}].splitBps\` must be in [0, 10000]`);
        }
        return { agent: getAddress(agent), splitBps: bps };
    });
    const data = encodeFunctionData({
        abi: SYNDICATE_GOVERNOR_ABI,
        functionName: "propose",
        args: [
            vault,
            strategy,
            args.metadataURI,
            strategyDuration,
            executeCalls,
            settlementCalls,
            coProposers,
        ],
    });
    return {
        txs: [governorTx(governor, chainId, data)],
        preconditions: [
            { type: "vault-not-locked", vault },
        ],
        description: `Propose a strategy on ${vault} (duration ${strategyDuration.toString()}s). Agent fee is snapshotted from the vault's agentFeeBps at propose time, then clamped to maxPerformanceFeeBps at settlement.`,
        note: "Reverts if the vault has an active proposal (cooldown not elapsed). The strategy address is immutable post-propose; pass address(0) to opt out of live NAV. `coProposers[].splitBps` total must satisfy contract-side constraints.",
    };
}
// ── Emergency settlement (GovernorEmergency, V1.5) ───────
export function encodeUnstick(args, chainId) {
    const governor = parseGovernor(args.governor);
    const id = parseProposalId(args.proposalId);
    const data = encodeFunctionData({
        abi: SYNDICATE_GOVERNOR_ABI,
        functionName: "unstick",
        args: [id],
    });
    return {
        txs: [governorTx(governor, chainId, data)],
        preconditions: [],
        description: `Unstick proposal ${id.toString()} — run its governance-approved settlement calls (owner-instant, no funds beyond the approved calls).`,
        note: "Vault-owner only. Valid only when the proposal is Executed AND its strategy duration has elapsed. Runs the proposal's pre-committed settlement calls and finalizes settlement; no owner stake required. Reverts if those calls revert — if they can't succeed, open an emergency-settle with custom unwind calls instead.",
    };
}
export function encodeEmergencySettle(args, chainId) {
    const governor = parseGovernor(args.governor);
    const id = parseProposalId(args.proposalId);
    if (!Array.isArray(args.calls)) {
        throw usage("`calls` must be an array of {target, data, value} objects");
    }
    const calls = args.calls.map((c, i) => validateBatchCall(c, "calls", i));
    const data = encodeFunctionData({
        abi: SYNDICATE_GOVERNOR_ABI,
        functionName: "emergencySettleWithCalls",
        args: [id, calls],
    });
    return {
        txs: [governorTx(governor, chainId, data)],
        preconditions: [],
        description: `Open an emergency-settle review on proposal ${id.toString()} with ${calls.length} unwind call(s).`,
        note: "Vault-owner only, requires bonded owner stake. Does NOT execute immediately — it commits the calls' hash and opens a guardian-reviewed window (default ~24h). After the window, finalize with finalizeEmergencySettle; guardians can block within it (owner stake burned on block-quorum).",
    };
}
// ── Parameter setters (GovernorParameters, V1.5) ─────────
function parseUintArg(value, label) {
    if (typeof value === "bigint") {
        if (value < 0n)
            throw usage(`\`${label}\` must be a non-negative integer`);
        return value;
    }
    if (typeof value === "number") {
        if (!Number.isInteger(value) || value < 0) {
            throw usage(`\`${label}\` must be a non-negative integer`);
        }
        return BigInt(value);
    }
    if (!/^\d+$/.test(value)) {
        throw usage(`\`${label}\` must be a non-negative integer string, got "${value}"`);
    }
    return BigInt(value);
}
/**
 * Maps a friendly parameter name to its on-chain setter. Every setter takes a
 * single uint256 and is owner-instant (V1.5 dropped the on-chain timelock).
 */
const GOVERNOR_PARAM_SETTERS = {
    votingPeriod: "setVotingPeriod",
    executionWindow: "setExecutionWindow",
    vetoThresholdBps: "setVetoThresholdBps",
    maxPerformanceFeeBps: "setMaxPerformanceFeeBps",
    maxStrategyDuration: "setMaxStrategyDuration",
    cooldownPeriod: "setCooldownPeriod",
};
export function encodeGovernorParam(args, chainId) {
    const governor = parseGovernor(args.governor);
    const fn = GOVERNOR_PARAM_SETTERS[args.param];
    if (!fn) {
        throw usage(`Unknown governor param "${args.param}" — must be one of: ${Object.keys(GOVERNOR_PARAM_SETTERS).join(", ")}`);
    }
    const value = parseUintArg(args.value, args.param);
    const data = encodeFunctionData({
        abi: SYNDICATE_GOVERNOR_ABI,
        functionName: fn,
        args: [value],
    });
    return {
        txs: [governorTx(governor, chainId, data)],
        preconditions: [],
        description: `Set governor parameter ${args.param} = ${value.toString()}.`,
        note: "Vault-owner only. V1.5 has no on-chain parameter timelock — the change applies immediately on this call. Bounds are validated on-chain; out-of-range values revert.",
    };
}
/**
 * Encode `ProtocolConfig.setProtocolFeeBps(value)`. The protocol fee lives on
 * the protocol-wide ProtocolConfig contract (resolved via
 * `factory.protocolConfig()`), NOT on a per-vault governor — sending this
 * selector to a governor reverts. Protocol-owner (multisig) only.
 */
export function encodeProtocolFeeBps(args, chainId) {
    if (!isAddress(args.protocolConfig)) {
        throw usage(`\`protocolConfig\` must be a valid address, got "${args.protocolConfig}"`);
    }
    const protocolConfig = getAddress(args.protocolConfig);
    const value = parseUintArg(args.value, "protocolFeeBps");
    const data = encodeFunctionData({
        abi: PROTOCOL_CONFIG_ABI,
        functionName: "setProtocolFeeBps",
        args: [value],
    });
    return {
        txs: [governorTx(protocolConfig, chainId, data)],
        preconditions: [],
        description: `Set ProtocolConfig protocolFeeBps = ${value.toString()}.`,
        note: "Protocol-owner only (the protocol multisig), NOT the vault owner. Targets the protocol-wide ProtocolConfig contract. Bounds are validated on-chain; setting a non-zero protocolFeeBps reverts unless protocolFeeRecipient is already set.",
    };
}
